import { debounce, get, isFinite, isPlainObject } from "es-toolkit/compat";
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from "react";
import type {
  DropdownContextType,
  DropdownData,
  DropdownProps,
  DropdownValueType,
  InternalDropdownPagination,
} from "./types";

const DROPDOWN_PAGE_SIZE = 100;

const isMultiValue = (
  hasGroups: boolean,
  value?: DropdownValueType
): value is string[] => {
  const valueToEvaluate = hasGroups ? value?.[1] : value;
  return (
    Array.isArray(valueToEvaluate) &&
    valueToEvaluate.every((e) => typeof e === "string" || isFinite(e))
  );
};

const isSingleValue = (
  hasGroups: boolean,
  value?: DropdownValueType
): value is string => {
  const valueToEvaluate = hasGroups ? value?.[1] : value;
  return typeof valueToEvaluate === "string" || isFinite(valueToEvaluate);
};

export const DropdownContext = createContext<DropdownContextType | null>(null);

export const useDropdownContext = () => {
  const ctx = useContext(DropdownContext);

  if (ctx === null) {
    throw new Error("Must be called within a DropdownProvider.");
  }

  return ctx;
};

export const DropdownProvider = ({
  children,
  ...restProps
}: {
  children: React.ReactNode;
} & DropdownProps) => {
  // REVIEW: defaultValue needs to be propagated here - so instead we rely on
  // value being updated and handle initial selection in an effect - essentially
  // after many re-renders and causing more
  const {
    name,
    value,
    isMulti = false,
    isLoading,
    isDisabled,
    labelKey,
    valueKey,
    options,
    onFetchData,
    onChange: onChangeProp,
    isClearable = true,
    refetch = undefined,
    onScroll,
    searchTerm,
    onSearchTermChange,
    groups,
    defaultGroup,
    isSearchable = true,
    noSearchDebounce,
    defaultOpen = false,
  } = restProps;

  const searchDebounceTime: number = useMemo(
    () => (noSearchDebounce ? 0 : 300),
    [noSearchDebounce]
  );

  const getValue = (option: Record<string, any> | string) => {
    if (!isPlainObject(option)) {
      return typeof option === "string" || typeof option === "number"
        ? String(option)
        : "";
    } else {
      return get(option, valueKey!);
    }
  };

  const getLabel = (option: Record<string, any> | string) => {
    if (!isPlainObject(option)) {
      return typeof option === "string" || typeof option === "number"
        ? String(option)
        : "n/a";
    } else {
      return get(option, labelKey!);
    }
  };

  const hasGroups = Array.isArray(groups) && groups.length > 0;

  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const clearTriggerRef = useRef<HTMLButtonElement | null>(null);
  const triggerRef = useRef<HTMLDivElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const [isMenuOpen, setIsMenuOpen] = useState(defaultOpen);
  const [activeGroup, setActiveGroupState] = useState<string | null>(
    defaultGroup ?? getValue(groups?.[0]) ?? ""
  );

  const dataReducer = (
    oldState: DropdownData,
    newState: Partial<DropdownData>
  ) => {
    const newData = {
      ...oldState,
      ...newState,
    };

    return newData;
  };

  // IMPROVE: this reducer is unnecessary and all related logic is horrendous.
  // Rip out in the future when the dropdown input is completely overhauled.
  const [data, setData] = useReducer(dataReducer, {
    isLoading: false,
    options: onFetchData ? [] : options,
    selected: [],
  });
  const paginationReducer = (
    oldState: InternalDropdownPagination,
    newState: Partial<InternalDropdownPagination>
  ) => {
    const newPagination: InternalDropdownPagination = {
      ...oldState,
      ...newState,
    };

    if (newState.searchTerm && newState.searchTerm !== oldState.searchTerm) {
      newPagination.page = 1;
    }

    // Used to prevent loading indicator when theres a search term and a user clicks off (normally after selection)
    if (
      newState.searchTerm === "" &&
      oldState.searchTerm !== newState.searchTerm
    ) {
      newPagination.isSearchTermReset = true;
    } else {
      newPagination.isSearchTermReset = false;
    }

    return newPagination;
  };

  const [pagination, setPagination] = useReducer(paginationReducer, {
    pageSize: DROPDOWN_PAGE_SIZE,
    page: 1,
    searchTerm: "",
    isLastPage: false,
    isSearchTermReset: false,
  });

  /**
   * Coerces the value to be valid for all the children of this component
   * @param value a value compatible with this dropdown component. Can be a string, an array of strings, or a tuple consisting of a group (string) and value (string).
   * @returns the value as-is if valid, value coerced to fit single/multi-select, or one of two if value is completely invalid: an empty string if it's a single select, or an empty array if it's a multi-select
   */
  const coerceValue = (value?: DropdownValueType): any => {
    if (hasGroups) {
      if (!value) {
        return [activeGroup ?? "", isMulti ? [] : ""];
      }

      if (Array.isArray(value)) {
        const [group, groupValue] = value;
        return [
          group,
          isMulti
            ? Array.isArray(groupValue)
              ? groupValue
              : [groupValue]
            : groupValue,
        ];
      }

      return [
        activeGroup,
        isMulti ? (Array.isArray(value) ? value : [value]) : value,
      ];
    }

    // if this prop isn't enabled
    if (!isMulti) {
      // and value is truthy, let it pass-through or coerce to an empty string
      return value ? value : "";
    }

    // if value is falsy, coerce to an empty array
    if (!value) {
      return [];
    }

    // otherwise, coerce value to be in an array if it isn't already one
    return Array.isArray(value) ? value : [value];
  };
  /**
   * Updates the selected options based on the target value.
   * @param targetValue - the value(s) that was selected/unselected.
   * @returns For single select: the matching object.
   *          For multi-select: an array of updated selected options.
   */
  const updateSelectedOptions = (targetValue: string | string[]) => {
    if (!isMulti) {
      return data.options?.find(
        (option: any) => targetValue === getValue(option)
      );
    }

    const selectedOptions = Array.isArray(data.selected)
      ? data.selected
      : [data.selected];

    const merged = [
      ...selectedOptions,
      ...(data.options ?? []).filter(
        (option: any) =>
          !selectedOptions.find(
            (selectedOption) => getValue(selectedOption) === getValue(option)
          )
      ),
    ].filter((option) =>
      Array.isArray(targetValue)
        ? targetValue.includes(getValue(option))
        : getValue(option) === targetValue
    );

    return merged;
  };

  const onChange = (newValue: DropdownValueType) => {
    const coercedValue = coerceValue(newValue);
    const [group, targetValue] = hasGroups
      ? coercedValue
      : [null, coercedValue];

    const selectedOptions = updateSelectedOptions(targetValue);

    setData({ selected: selectedOptions });
    onChangeProp?.(hasGroups ? [group, targetValue] : targetValue);
  };

  // coerce value to make sure the value is always valid and memoize it to prevent cascading re-renders
  const coercedValue = useMemo(() => {
    return coerceValue(value);
  }, [value]);

  /**
   * Finds the target value object within an array of options.
   * @param options - an array of option objects to search through.
   * @param targetValue - the target value to search find an object for.
   * @returns For single select: the matching object if found.
   *          For multi-select: the matching objects if found.
   */
  const findSelectedOptions = (
    options: any[],
    targetValue: string | string[]
  ) => {
    // If it's not a multi-select, find the option that matches the value
    if (!isMulti) {
      return options.find((option) => targetValue === getValue(option)) ?? [];
    }

    // If it's a multi-select, find all the options that match the values
    return (
      options.filter((option: any) =>
        coerceValue(targetValue)?.includes(getValue(option))
      ) ?? []
    );
  };

  const fetchInitialSelected = useCallback(
    debounce(async (value) => {
      let selectedOptions = [];
      const selectedValue = hasGroups ? value?.[1] : value;

      // If loading data from the backend
      if (!onFetchData) {
        selectedOptions = findSelectedOptions(options ?? [], selectedValue);
      } else {
        if (selectedValue?.length || selectedValue) {
          if (!isMulti) {
            const res = await onFetchData({
              value: selectedValue,
            });

            selectedOptions = findSelectedOptions(res ?? [], selectedValue);
          } else {
            selectedOptions = await onFetchData({
              value: selectedValue,
            }).then((resp) =>
              resp.filter((option: any) =>
                Array.isArray(selectedValue)
                  ? selectedValue.includes(getValue(option))
                  : getValue(option) === selectedValue
              )
            );
          }
        }
      }
      setData({
        selected: selectedOptions,
      });
    }, searchDebounceTime),
    [options, onFetchData, searchDebounceTime]
  );

  // REVIEW: initial selection only happens inside an effect? this needs cleanup
  useEffect(() => {
    if (onFetchData || options?.length) {
      fetchInitialSelected(value);
    }

    return () => {
      fetchInitialSelected.cancel();
    };
  }, [value, onFetchData, options, fetchInitialSelected]);

  const fetchData = useCallback(
    debounce(async (newPagination) => {
      setData({ isLoading: true });

      const newOptions = await onFetchData?.(newPagination);

      if (!newOptions) {
        return;
      }

      if (newOptions.length < DROPDOWN_PAGE_SIZE) {
        setData({ isLastPage: true });
      }

      if (newPagination.page === 1) {
        setData({ options: newOptions, isLoading: false });
      } else {
        setData({
          options: [...data.options, ...newOptions],
          isLoading: false,
        });
      }
    }, searchDebounceTime),
    [data.options, onFetchData, searchDebounceTime]
  );

  useEffect(() => {
    if (onFetchData) {
      fetchData(pagination);
    }
  }, [pagination, refetch, activeGroup]);

  const setActiveGroup = (newActiveGroup: string | null) => {
    setPagination?.({ searchTerm: "", page: 1 });
    onChange([newActiveGroup ?? "", isMulti ? [] : ""]);
    setActiveGroupState(newActiveGroup);
  };

  useEffect(() => {
    if (options) {
      setData({
        options,
      });
    }
  }, [options]);

  const handleOpenMenu = () => {
    if (isDisabled || isLoading) {
      return;
    }

    setIsMenuOpen(true);
  };

  const handleCloseMenu = () => {
    setIsMenuOpen(false);
  };

  const checkForUnknownSelectedValue = () => {
    /*
      Make sure that with grouping enabled, if the user selects another group and
      doesn't select anything from the new group, when closing the dropdown,
      default to "empty" value ('' or [] based on isMulti)
    */
    if (hasGroups && value) {
      const selectedValue = value[1];

      // Check if selected value exists in current options
      const doesValueExistinCurrentOptions = isMulti
        ? data.options?.some((option: any) =>
            selectedValue.includes(getValue(option))
          )
        : data.options?.some(
            (option: any) => getValue(option) === selectedValue
          );

      if (!doesValueExistinCurrentOptions) {
        onClear();
      }
    }
  };

  const onClear = () => {
    onSearchTermChange?.("");
    setPagination?.({ searchTerm: "" });
    onChange?.(isMulti ? [] : "");
    handleCloseMenu();
  };

  return (
    <DropdownContext.Provider
      value={{
        ...restProps,
        isMulti,
        name,
        getLabel,
        getValue,
        value: coercedValue,
        hasValue: Boolean(
          (!isMulti && isSingleValue(hasGroups, value) && value) ||
            (isMulti && isMultiValue(hasGroups, value) && value?.length)
        ),
        isBusy: Boolean(restProps.isBusy ?? data?.isLoading),
        isMultiValue,
        isSingleValue,
        triggerRef,
        searchInputRef,
        contentRef,
        clearTriggerRef,
        isMenuOpen,
        openMenu: handleOpenMenu,
        closeMenu: handleCloseMenu,
        clear: onClear,
        pagination,
        setPagination,
        data,
        setData,
        onChange,
        isClearable,
        onScroll,
        searchTerm,
        onSearchTermChange,
        activeGroup,
        setActiveGroup,
        hasGroups,
        checkForUnknownSelectedValue,
        isSearchable,
      }}
    >
      {children}
    </DropdownContext.Provider>
  );
};
